/**
 * Detailed user profile view component
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { teamApi } from '@/api/team';
import { userApi } from '@/api/user';
import { useAuth } from '@/hooks/useAuth';
import type { UserDetails, UpdateUserRequest, UserTeamRole, TeamRole } from '@/types/team';
import { LinkedInConnection, ActionLimitsControl } from '@/components';
import { UserStatistics } from './UserStatistics';
import { DisconnectUnipileConfirmModal } from '@/components/DisconnectUnipileConfirmModal';
import { UserSyncJobsList } from './UserSyncJobsList';

// Custom impersonate icon component
const ImpersonateIcon = ({ fontSize = 'small' }: { fontSize?: 'small' | 'medium' | 'large' }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <PersonIcon fontSize={fontSize} />
    <Box 
      component="span"
      sx={{ 
        position: 'absolute', 
        left: -4, 
        top: '40%', 
        transform: 'translateY(-50%)',
        fontSize: fontSize === 'small' ? '0.7rem' : fontSize === 'medium' ? '0.9rem' : '1.1rem',
        fontWeight: 'bold'
      }} 
    >
      →
    </Box>
  </Box>
);

interface UserDetailViewProps {
  user: UserDetails;
  onBack: () => void;
  onImpersonate: (userUuid: string) => void;
}

interface EditableFields {
  name: string;
  email: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  is_staff: boolean;
  is_superuser: boolean;
  unipile_id: string;
}

interface NewTeamRole {
  team_uuid: string;
  role: 'OWNER' | 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
}

export const UserDetailView: React.FC<UserDetailViewProps> = ({ user, onBack, onImpersonate }) => {
  const { user: currentUser } = useAuth();
  const userId = user.key;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize activeTab from URL params, default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        return tabIndex;
      }
    }
    return 0;
  });

  // Fetch user details with statistics when on statistics tab
  const {
    data: userWithStats,
    isLoading: isLoadingUserStats,
  } = useQuery({
    queryKey: ['user-details', userId, activeTab === 2],
    queryFn: () => teamApi.getUserDetails(userId as string, activeTab === 2),
    enabled: !!userId && activeTab === 2,
  });

  // Use user with stats if available, otherwise use the prop user
  const displayUser = activeTab === 2 && userWithStats ? userWithStats : user;

  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);

  // Check if current user can edit account settings (admin/staff only)
  const canEditAccountSettings = currentUser?.is_staff || currentUser?.is_superuser;
  const canManageActions = !!(canEditAccountSettings || currentUser?.key === userId);

  const [isEditing, setIsEditing] = useState(false);
  const [editableFields, setEditableFields] = useState<EditableFields>({
    name: user.name || '',
    email: user.email || '',
    status: user.status as 'ACTIVE' | 'SUSPENDED' | 'DELETED',
    is_staff: user.is_staff || false,
    is_superuser: user.is_superuser || false,
    unipile_id: user.unipile_id || '',
  });
  const [newTeamRoles, setNewTeamRoles] = useState<NewTeamRole[]>([]);
  // Local state for project roles to enable real-time updates
  const [teamRoles, setTeamRoles] = useState<UserTeamRole[]>(user.team_roles || []);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isRefreshingLinkedIn, setIsRefreshingLinkedIn] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isWithdrawInvitesDialogOpen, setIsWithdrawInvitesDialogOpen] = useState(false);
  const [isWithdrawInvitesPending, setIsWithdrawInvitesPending] = useState(false);

  // Reset editable fields and project roles when user changes, but only if not editing
  // This prevents overwriting user's changes when the user prop updates after a save
  useEffect(() => {
    if (!isEditing) {
      setEditableFields({
        name: user.name || '',
        email: user.email || '',
        status: user.status as 'ACTIVE' | 'SUSPENDED' | 'DELETED',
        is_staff: user.is_staff || false,
        is_superuser: user.is_superuser || false,
        unipile_id: user.unipile_id || '',
      });
      setTeamRoles(user.team_roles || []);
      setNewTeamRoles([]);
    }
  }, [user, isEditing]);

  // Fetch LinkedIn status for the user
  const {
    data: linkedInStatus,
    isLoading: isLoadingLinkedIn,
  } = useQuery({
    queryKey: ['user-linkedin-status', userId],
    queryFn: () => teamApi.getUserLinkedInStatus(userId as string),
    enabled: !!userId,
  });

  // Fetch available projects for adding roles
  // Enable for admin/staff when editing, or always for SERVICE_MANAGER users
  const { data: projectsData, isLoading: isLoadingTeams } = useQuery({
    queryKey: ['user-detail-teams', currentUser?.key],
    queryFn: () =>
      teamApi.getTeams(
        currentUser?.is_staff || currentUser?.is_superuser
          ? { max_results: 1000 }
          : { max_results: 1000, role_name: 'SERVICE_MANAGER' }
      ),
    enabled: !!currentUser && (isEditing || !canEditAccountSettings),
  });

  const availableTeams =
    projectsData?.output?.items?.map((p: any) => ({
      uuid: p.key,
      name: p.name || p.key,
    })) || [];

  // Filter out projects that user already has roles in (use local state)
  const existingTeamUuids = new Set(teamRoles.map(r => r.team_uuid));
  // Also exclude projects that are pending to be added
  const pendingTeamUuids = new Set(newTeamRoles.map(r => r.team_uuid).filter(Boolean));
  const filteredTeams = availableTeams.filter(
    (p: { uuid: string }) => !existingTeamUuids.has(p.uuid) && !pendingTeamUuids.has(p.uuid)
  );

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserRequest) => teamApi.updateUser(userId as string, data),
    onSuccess: async (updatedUser, variables) => {
      queryClient.invalidateQueries({ queryKey: ['team-users'] });
      // Invalidate and refetch all user-details queries (including the one with currentUser?.key in the parent)
      await queryClient.invalidateQueries({ queryKey: ['user-details'] });
      
      // If unipile_id was updated, refresh LinkedIn status from source
      if (variables.unipile_id !== undefined && variables.unipile_id !== user.unipile_id) {
        // Invalidate LinkedIn status query to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['user-linkedin-status', userId] });
        // Also trigger a refresh from source to get the latest status with new unipile_id
        teamApi.getUserLinkedInStatus(userId as string, true).then((refreshed) => {
          queryClient.setQueryData(['user-linkedin-status', userId], refreshed);
        }).catch((error) => {
          console.error('Failed to refresh LinkedIn status after unipile_id update', error);
        });
      }
      
      // Wait for the query to refetch before updating editableFields
      // This ensures we have the latest user data from the parent
      await queryClient.refetchQueries({ queryKey: ['user-details'] });
      
      // Update editableFields with the returned data to keep them in sync
      // This ensures the next edit will compare against the latest data
      if (updatedUser) {
        setEditableFields({
          name: updatedUser.name || '',
          email: updatedUser.email || '',
          status: updatedUser.status as 'ACTIVE' | 'SUSPENDED' | 'DELETED',
          is_staff: updatedUser.is_staff || false,
          is_superuser: updatedUser.is_superuser || false,
          unipile_id: updatedUser.unipile_id || '',
        });
      }
      
      setIsEditing(false);
      setToast({
        open: true,
        message: 'User updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to update user',
        severity: 'error',
      });
    },
  });

  // Add project roles mutation using invite_user endpoint
  const addRolesMutation = useMutation({
    mutationFn: (newRoles: TeamRole[]) =>
      teamApi.inviteUser({
        user_uuid: userId,
        team_roles: newRoles,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['team-users'] });
      // Update local project roles state with new roles
      // Filter out OWNER (cannot be assigned) and map to UserTeamRole
      const addedRoles: UserTeamRole[] = variables
        .filter((role): role is TeamRole & { role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' } => 
          role.role !== 'OWNER'
        )
        .map((role, idx) => {
          const project = availableTeams.find((p: { uuid: string }) => p.uuid === role.team_uuid);
          return {
            key: `new-${Date.now()}-${idx}`, // Temporary key until page refresh
            team_uuid: role.team_uuid,
            team_name: project?.name || role.team_uuid,
            role: role.role,
            can_remove: role.role === 'SERVICE_MEMBER' || !!canEditAccountSettings,
          };
        });
      setTeamRoles(prev => [...prev, ...addedRoles]);
      setNewTeamRoles([]);
      setToast({
        open: true,
        message: 'Project roles added successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to add project roles',
        severity: 'error',
      });
    },
  });

  // Remove project role mutation
  const removeRoleMutation = useMutation({
    mutationFn: (roleUuid: string) => teamApi.removeTeamRole(roleUuid),
    onSuccess: (_data, roleUuid) => {
      queryClient.invalidateQueries({ queryKey: ['team-users'] });
      // Remove the role from local state
      setTeamRoles(prev => prev.filter(role => role.key !== roleUuid));
      setToast({
        open: true,
        message: 'Role removed successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to remove role',
        severity: 'error',
      });
    },
  });

  // Disconnect Unipile account mutation
  const disconnectUnipileMutation = useMutation({
    mutationFn: () => userApi.disconnectUnipileAccount(userId as string),
    onSuccess: async () => {
      // Invalidate and refetch to ensure button visibility updates immediately
      await queryClient.invalidateQueries({ queryKey: ['user-details', currentUser?.key, userId] });
      await queryClient.refetchQueries({ queryKey: ['user-details', currentUser?.key, userId] });
      queryClient.invalidateQueries({ queryKey: ['user-linkedin-status', userId] });
      setShowDisconnectModal(false);
      setToast({
        open: true,
        message: 'Unipile account disconnected successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.message || 'Failed to disconnect Unipile account',
        severity: 'error',
      });
    },
  });

  const handleFieldChange = (field: keyof EditableFields, value: any) => {
    setEditableFields(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Save user profile changes
    const updateData: UpdateUserRequest = {};

    // Use current user prop for comparison (it should be up-to-date after query refetch)
    const currentUser = user;

    if (editableFields.name !== (currentUser.name || '')) {
      updateData.name = editableFields.name;
    }
    if (editableFields.email !== (currentUser.email || '')) {
      updateData.email = editableFields.email;
    }
    if (editableFields.status !== currentUser.status) {
      updateData.status = editableFields.status;
    }
    if (currentUser.can_edit_system_role) {
      if (editableFields.is_staff !== currentUser.is_staff) {
        updateData.is_staff = editableFields.is_staff;
      }
      if (editableFields.is_superuser !== currentUser.is_superuser) {
        updateData.is_superuser = editableFields.is_superuser;
      }
    }
    if (editableFields.unipile_id !== (currentUser.unipile_id || '')) {
      updateData.unipile_id = editableFields.unipile_id;
    }

    // Save profile changes if any
    if (Object.keys(updateData).length > 0) {
      updateMutation.mutate(updateData);
    }

    // Save new project roles if any (filter out OWNER - cannot be assigned)
    const validNewRoles = newTeamRoles.filter(r => r.team_uuid && r.role && r.role !== 'OWNER');
    if (validNewRoles.length > 0) {
      addRolesMutation.mutate(validNewRoles as TeamRole[]);
    }

    if (Object.keys(updateData).length === 0 && validNewRoles.length === 0) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditableFields({
      name: user.name || '',
      email: user.email || '',
      status: user.status as 'ACTIVE' | 'SUSPENDED' | 'DELETED',
      is_staff: user.is_staff || false,
      is_superuser: user.is_superuser || false,
      unipile_id: user.unipile_id || '',
    });
    setNewTeamRoles([]);
    setIsEditing(false);
  };

  const handleRemoveRole = (roleUuid: string) => {
    removeRoleMutation.mutate(roleUuid);
  };

  const handleAddTeamRoleRow = () => {
    setNewTeamRoles([...newTeamRoles, { team_uuid: '', role: 'SERVICE_MEMBER' }]);
  };

  const handleRemoveTeamRoleRow = (index: number) => {
    setNewTeamRoles(newTeamRoles.filter((_, i) => i !== index));
  };

  const handleUpdateTeamRole = (index: number, field: 'team_uuid' | 'role', value: string) => {
    const updated = [...newTeamRoles];
    updated[index] = { ...updated[index], [field]: value };
    setNewTeamRoles(updated);
  };

  const redirectToPermanentLinkedInLink = () => {
    if (!userId) {
      return;
    }

    // When the user's Unipile ID is empty, use the generic reconnect link (no user_id).
    const baseUrl = `${window.location.origin}/reconnect-linkedin`;
    const reconnectUrl =
      user.unipile_id && userId ? `${baseUrl}/${userId}` : baseUrl;

    window.open(reconnectUrl, '_blank');
  };

  const handleLinkedInConnect = () => {
    redirectToPermanentLinkedInLink();
  };

  const handleLinkedInReconnect = () => {
    redirectToPermanentLinkedInLink();
  };

  const handleRefreshLinkedInStatus = async () => {
    if (!userId) return;
    setIsRefreshingLinkedIn(true);
    try {
      const refreshed = await teamApi.getUserLinkedInStatus(userId as string, true);
      queryClient.setQueryData(['user-linkedin-status', userId], refreshed);
      // Also refresh user details to pick up any updated unipile_id from DB
      // This ensures the disconnect button appears/disappears based on the latest unipile_id
      await queryClient.invalidateQueries({ queryKey: ['user-details', currentUser?.key, userId] });
      await queryClient.refetchQueries({ queryKey: ['user-details', currentUser?.key, userId] });
    } catch (error: any) {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to refresh LinkedIn status',
        severity: 'error',
      });
    } finally {
      setIsRefreshingLinkedIn(false);
    }
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const getRoleLabel = () => (user.is_superuser ? 'Superuser' : user.is_staff ? 'Staff' : 'User');
  const getRoleColor = () => (user.is_superuser ? 'error' : user.is_staff ? 'warning' : 'info');
  const getStatusColor = () => ({ ACTIVE: 'success', SUSPENDED: 'warning', DELETED: 'error' }[user.status] || 'default');

  // Determine available system roles based on current user's role
  const getAvailableSystemRoles = (): string[] => {
    if (currentUser?.is_superuser) {
      return ['user', 'staff', 'superuser'];
    } else if (currentUser?.is_staff) {
      return ['user', 'staff'];
    }
    return ['user'];
  };

  const getSystemRoleValue = (): string => {
    if (isEditing) {
      if (editableFields.is_superuser) return 'superuser';
      if (editableFields.is_staff) return 'staff';
      return 'user';
    }
    if (user.is_superuser) return 'superuser';
    if (user.is_staff) return 'staff';
    return 'user';
  };

  const handleSystemRoleChange = (value: string) => {
    handleFieldChange('is_superuser', value === 'superuser');
    handleFieldChange('is_staff', value === 'staff' || value === 'superuser');
  };

  const isPending = updateMutation.isPending || addRolesMutation.isPending;

  // For project roles, allow editing if:
  // - user is admin/staff (in editing mode), OR
  // - user is a SERVICE_MANAGER (they can manage roles in their projects without entering edit mode)
  const canEditTeamRoles = isEditing || !canEditAccountSettings;

  // Update URL when tab changes
  const updateURLTab = useCallback((tabIndex: number) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (tabIndex === 0) {
      // Remove tab param if it's the default (first tab)
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabIndex.toString());
    }
    
    setSearchParams(newSearchParams, { replace: true });
    // Reset flag after a short delay to allow URL to update
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return; // Skip if this is from our own internal update
    }

    const tabParam = searchParams.get('tab');
    let urlTabIndex = 0;
    if (tabParam !== null) {
      const parsedTab = parseInt(tabParam, 10);
      if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 3) {
        urlTabIndex = parsedTab;
      }
    }

    if (urlTabIndex !== activeTab) {
      setActiveTab(urlTabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    updateURLTab(newValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} size="small"><ArrowBackIcon /></IconButton>
          <Typography variant="h5" fontWeight={600}>User Profile</Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
          {/* Left Column - Profile Card */}
          <Box>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={user.profile_image || undefined}
                    sx={{ width: 80, height: 80, bgcolor: 'primary.main', mb: 2, fontSize: 36 }}
                  >
                    {(user.name && user.name.charAt(0).toUpperCase()) || (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {user.name || user.email || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {user.email || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip label={getRoleLabel()} color={getRoleColor() as any} size="small" />
                    <Chip label={user.status} color={getStatusColor() as any} size="small" variant="outlined" />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Impersonate Button */}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ImpersonateIcon />}
                  onClick={() => onImpersonate(userId as string)}
                  disabled={userId === currentUser?.key}
                  sx={{ mb: 2 }}
                >
                  Sign in as this user
                </Button>

                {/* LinkedIn Connection */}
                <LinkedInConnection
                  status={linkedInStatus || null}
                  isLoading={isLoadingLinkedIn}
                  onConnect={handleLinkedInConnect}
                  onReconnect={handleLinkedInReconnect}
                  onRefresh={handleRefreshLinkedInStatus}
                  isRefreshing={isRefreshingLinkedIn}
                  userId={userId as string}
                  hasUnipileId={!!user.unipile_id}
                />

                {/* Disconnect Button - Only show if user has unipile_id, account is connected, and current user can manage */}
                {user.unipile_id && linkedInStatus?.is_connected && (canEditAccountSettings || currentUser?.key === userId) && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowDisconnectModal(true)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Disconnect Unipile Account
                    </Button>
                  </Box>
                )}

              </CardContent>
            </Card>
          </Box>

          {/* Right Column - Settings */}
          <Card>
            <CardContent>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Account Settings" />
                <Tab label="Limits & Activity Control" />
                <Tab label="Statistics" />
                <Tab label="Actions" />
              </Tabs>

              {activeTab === 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Account Settings
                    </Typography>
                    {/* Only admin/staff can edit account settings */}
                    {canEditAccountSettings && (
                      !isEditing ? (
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => setIsEditing(true)}
                        >
                          Edit Profile
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={isPending}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            disabled={isPending}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )
                    )}
                  </Box>

              {/* Basic Info Fields */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <TextField
                  label="Full Name"
                  value={isEditing ? editableFields.name : (user.name || '')}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  disabled={!isEditing || isPending}
                  fullWidth
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <TextField
                  label="Email"
                  value={isEditing ? editableFields.email : (user.email || '')}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  disabled={!isEditing || isPending}
                  fullWidth
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <TextField
                  label="Unipile ID"
                  value={isEditing ? editableFields.unipile_id : (user.unipile_id || '')}
                  onChange={(e) => handleFieldChange('unipile_id', e.target.value)}
                  disabled={!isEditing || isPending}
                  fullWidth
                  helperText="Unipile account identifier"
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <FormControl fullWidth disabled={!isEditing || isPending}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={isEditing ? editableFields.status : user.status}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                    <MenuItem value="DELETED">Deleted</MenuItem>
                  </Select>
                </FormControl>

                {/* System Role - Only visible to admin/staff */}
                {user.can_edit_system_role && (
                  <FormControl fullWidth disabled={!isEditing || isPending}>
                    <InputLabel>System Role</InputLabel>
                    <Select
                      value={getSystemRoleValue()}
                      onChange={(e) => handleSystemRoleChange(e.target.value)}
                      label="System Role"
                    >
                      {getAvailableSystemRoles().map((role) => (
                        <MenuItem key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Team Roles Section */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Team Roles
                  </Typography>
                  {canEditTeamRoles && filteredTeams.length > 0 && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddTeamRoleRow}
                      size="small"
                      disabled={isPending || isLoadingTeams}
                    >
                      Add Role
                    </Button>
                  )}
                </Box>

                {/* Existing Project Roles - use local state */}
                {teamRoles.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {teamRoles.map((role: UserTeamRole) => (
                      <Box
                        key={role.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {role.team_name}
                          </Typography>
                          <Chip
                            label={role.role === 'OWNER' ? 'Owner' : role.role === 'SERVICE_MANAGER' ? 'Manager' : 'Member'}
                            color={role.role === 'OWNER' || role.role === 'SERVICE_MANAGER' ? 'primary' : 'default'}
                            size="small"
                          />
                        </Box>
                        {canEditTeamRoles && role.can_remove && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveRole(role.key)}
                            disabled={removeRoleMutation.isPending}
                          >
                            {removeRoleMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No team roles assigned
                  </Typography>
                )}

                {/* New Project Roles (when adding) */}
                {canEditTeamRoles && newTeamRoles.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        New roles to add:
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const validNewRoles = newTeamRoles.filter(r => r.team_uuid && r.role && r.role !== 'OWNER');
                          if (validNewRoles.length > 0) {
                            addRolesMutation.mutate(validNewRoles as TeamRole[]);
                          }
                        }}
                        disabled={isPending || newTeamRoles.every(r => !r.team_uuid)}
                        startIcon={isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                      >
                        Save Roles
                      </Button>
                    </Box>
                    {newTeamRoles.map((teamRole, index) => {
                      // Include the selected team in options even if it's filtered out
                      const selectedTeam = teamRole.team_uuid 
                        ? availableTeams.find((t: { uuid: string }) => t.uuid === teamRole.team_uuid)
                        : null;
                      const selectOptions = selectedTeam && !filteredTeams.find((t: { uuid: string }) => t.uuid === selectedTeam.uuid)
                        ? [selectedTeam, ...filteredTeams]
                        : filteredTeams;
                      
                      return (
                      <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl sx={{ flex: 1 }} size="small">
                          <InputLabel>Team</InputLabel>
                          <Select
                            value={teamRole.team_uuid}
                            onChange={(e) => handleUpdateTeamRole(index, 'team_uuid', e.target.value)}
                            label="Team"
                            disabled={isPending || isLoadingTeams}
                          >
                            {selectOptions.map((team: { uuid: string; name: string }) => (
                              <MenuItem key={team.uuid} value={team.uuid}>
                                {team.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 150 }} size="small">
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={teamRole.role}
                            onChange={(e) => handleUpdateTeamRole(index, 'role', e.target.value)}
                            label="Role"
                            disabled={isPending}
                          >
                            <MenuItem value="SERVICE_MANAGER">Manager</MenuItem>
                            <MenuItem value="SERVICE_MEMBER">Member</MenuItem>
                          </Select>
                        </FormControl>
                        <IconButton
                          onClick={() => handleRemoveTeamRoleRow(index)}
                          disabled={isPending}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      );
                    })}
                  </Box>
                )}

                {canEditTeamRoles && filteredTeams.length === 0 && !isLoadingTeams && newTeamRoles.length === 0 && teamRoles.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No more teams available to add roles for.
                  </Typography>
                )}
              </Box>
                </>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 1 }}>
                  <ActionLimitsControl scope="user" resourceId={userId as string} />
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ mt: 1 }}>
                  {isLoadingUserStats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <UserStatistics user={displayUser} />
                  )}
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ mt: 1 }}>
                  <UserSyncJobsList
                    userUuid={userId as string}
                    isConnected={linkedInStatus?.is_connected || false}
                    hasUnipileId={!!user.unipile_id}
                    canManageActions={canManageActions}
                    onCreateJob={(fromDate: string, toDate: string) => {
                      userApi.createSyncMessagesJob(userId as string, fromDate, toDate).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['user-sync-jobs', currentUser?.key, userId] });
                        setToast({
                          open: true,
                          message: 'Sync job created successfully',
                          severity: 'success',
                        });
                      }).catch((error: any) => {
                        const errorMessage = error.response?.data?.error_messages?.[0] || error.response?.data?.message || 'Failed to create sync job';
                        setToast({
                          open: true,
                          message: errorMessage,
                          severity: 'error',
                        });
                      });
                    }}
                    onWithdrawPendingInvites={() => setIsWithdrawInvitesDialogOpen(true)}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Disconnect Confirmation Modal */}
      <DisconnectUnipileConfirmModal
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={() => disconnectUnipileMutation.mutate()}
        isDisconnecting={disconnectUnipileMutation.isPending}
        userName={user.name || undefined}
      />

      <Dialog
        open={isWithdrawInvitesDialogOpen}
        onClose={() => setIsWithdrawInvitesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw pending invites</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to withdraw all pending invites for this user? This will
            schedule a job to process them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsWithdrawInvitesDialogOpen(false)}
            disabled={isWithdrawInvitesPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PersonRemoveIcon />}
            onClick={() => {
              setIsWithdrawInvitesPending(true);
              userApi
                .createWithdrawPendingInvitesJob(userId as string)
                .then(() => {
                  queryClient.invalidateQueries({ queryKey: ['user-sync-jobs', currentUser?.key, userId] });
                  setToast({
                    open: true,
                    message: 'Withdraw pending invites job created successfully',
                    severity: 'success',
                  });
                })
                .catch((error: any) => {
                  const errorMessage =
                    error.response?.data?.error_messages?.[0] ||
                    error.response?.data?.message ||
                    'Failed to create withdraw job';
                  setToast({
                    open: true,
                    message: errorMessage,
                    severity: 'error',
                  });
                })
                .finally(() => {
                  setIsWithdrawInvitesPending(false);
                  setIsWithdrawInvitesDialogOpen(false);
                });
            }}
            disabled={isWithdrawInvitesPending}
          >
            Withdraw pending invites
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
